import { type FC, useEffect } from 'react';

import MarketoForm from 'molecules/Form';
import Image from 'molecules/Image';

import DashboardMockup from 'components/DashboardMockup';
import { useFreeTrialState } from 'components/FreeTrialFTUX/hooks/useFreeTrialState';
import IntegrationStep from 'components/FreeTrialFTUX/steps/IntegrationStep';
import SignUpStep from 'components/FreeTrialFTUX/steps/SignUpStep';
import UseCaseStep from 'components/FreeTrialFTUX/steps/UseCaseStep';
import VideoStep from 'components/FreeTrialFTUX/steps/VideoStep';

import { useBuilderReference } from 'utils/hooks/useBuilderReference';
import { useHideGlobalChrome } from 'utils/layout/useImmersiveChrome';

import type { FreeTrialFTUXProps } from 'components/FreeTrialFTUX/config';
import type { UseCaseOption } from 'components/FreeTrialFTUX/config/types';

const FreeTrialFTUX: FC<FreeTrialFTUXProps> = props => {
  const {
    signUpForm,
    enrichmentForm,
    signUpHeading = 'Start your free trial now',
    signUpSubheading = 'Get instant access to your own Prismatic sandbox.',
    useCaseHeading = 'Welcome',
    useCaseDescription = 'Tell us a little about yourself so we can send you down the right path.',
    useCaseQuestion = "What's your primary use case?",
    useCaseOptions,
    integrationHeading = 'Welcome',
    integrationDescription = "Tell us a little about what you're building.",
    integrationQuestion = 'What integration do you want to build first?',
    integrationPlaceholder = 'Microsoft Dynamics 365...',
    videoHeading = 'A quick video',
    videoDescription = 'Familiarize yourself with Prismatic terms and get the lay of the land.',
    videoCaption = 'Watch a 1 minute Prismatic orientation:',
    videoSrc,
    videoThumbnail,
    thankYouUrl = '/lp/free-trial-thank-you/',
    nextButtonText = 'Next',
    skipButtonText = 'Skip',
  } = props;

  useHideGlobalChrome();

  const signUpFormRef = signUpForm as FormReference | undefined;
  const signUpFormPreloaded = signUpFormRef?.value?.data as FormReference | undefined;
  const signUpFormFetched = useBuilderReference<FormReference>(
    signUpFormPreloaded ? undefined : signUpFormRef?.model,
    signUpFormPreloaded ? undefined : signUpFormRef?.id,
  );
  const signUpFormData = signUpFormPreloaded || signUpFormFetched || undefined;

  const enrichmentFormRef = enrichmentForm as FormReference | undefined;
  const enrichmentFormPreloaded = enrichmentFormRef?.value?.data as FormReference | undefined;
  const enrichmentFormFetched = useBuilderReference<FormReference>(
    enrichmentFormPreloaded ? undefined : enrichmentFormRef?.model,
    enrichmentFormPreloaded ? undefined : enrichmentFormRef?.id,
  );
  const enrichmentFormData = enrichmentFormPreloaded || enrichmentFormFetched || undefined;
  const enrichmentFormId = enrichmentFormData?.formId || '';

  const {
    currentStep,
    answers,
    isHydrating,
    isTransitioning,
    isRedirecting,
    handleSignUpSuccess,
    handleUseCaseAnswer,
    handleUseCaseSkip,
    handleIntegrationSubmit,
    handleIntegrationSkip,
    handleVideoNext,
    handleVideoSkip,
  } = useFreeTrialState({
    enrichmentFormId,
    thankYouUrl,
  });

  const options = (useCaseOptions || []) as UseCaseOption[];

  useEffect(() => {
    // When the user navigates away and returns via the browser back/forward
    // cache, restore can leave the page with stale Next.js chunks and a stale
    // completed-flow state. Force a fresh load so the user lands on the sign-up
    // step cleanly.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const renderStep = () => {
    if (isHydrating || isRedirecting) {
      return (
        <div className="flex h-96 items-center justify-center">
          <div className="aspect-square w-12 animate-loading rounded-full border-8 border-primary-300" />
        </div>
      );
    }

    switch (currentStep) {
      case 'signUp':
        return (
          <SignUpStep
            formData={signUpFormData}
            heading={signUpHeading}
            subheading={signUpSubheading}
            onSuccess={handleSignUpSuccess}
            isTransitioning={isTransitioning}
          />
        );
      case 'useCase':
        return (
          <UseCaseStep
            heading={useCaseHeading}
            description={useCaseDescription}
            question={useCaseQuestion}
            options={options}
            initialValue={answers.useCase}
            nextLabel={nextButtonText}
            skipLabel={skipButtonText}
            onAnswer={handleUseCaseAnswer}
            onSkip={handleUseCaseSkip}
            isTransitioning={isTransitioning}
          />
        );
      case 'integration':
        return (
          <IntegrationStep
            heading={integrationHeading}
            description={integrationDescription}
            question={integrationQuestion}
            placeholder={integrationPlaceholder}
            initialValue={answers.integration}
            nextLabel={nextButtonText}
            skipLabel={skipButtonText}
            onSubmit={handleIntegrationSubmit}
            onSkip={handleIntegrationSkip}
            isTransitioning={isTransitioning}
          />
        );
      case 'video':
        return (
          <VideoStep
            heading={videoHeading}
            description={videoDescription}
            caption={videoCaption}
            videoSrc={videoSrc}
            videoThumbnail={videoThumbnail}
            nextLabel={nextButtonText}
            skipLabel={skipButtonText}
            onNext={handleVideoNext}
            onSkip={handleVideoSkip}
            isTransitioning={isTransitioning}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative flex min-h-dvh w-full flex-col overflow-hidden bg-gray-900">
      {/* Server-rendered page heading for crawlers: the visible step headings
          only mount after hydration (the server renders the loading spinner),
          so without this the page has no heading at all in the SSR HTML. */}
      <h1 className="sr-only">{signUpHeading}</h1>
      <div className="absolute inset-0 hidden md:block">
        <DashboardMockup />
      </div>

      <div className="absolute inset-0 bg-gray-900/70" aria-hidden />

      <div className="relative z-10 flex h-18 shrink-0 items-center justify-center px-4 md:hidden">
        <Image
          url="/prismatic-logo.svg"
          alt="Prismatic logo"
          width={140}
          height={29}
          noFill
          priority
          className="h-auto w-[140px] object-contain"
        />
      </div>

      <div className="relative flex flex-1 items-start justify-center px-4 pb-6 md:items-center md:p-10">
        <div className="w-full max-w-[832px] overflow-hidden rounded-lg bg-white">{renderStep()}</div>
      </div>

      {enrichmentFormData && (
        <div
          className="pointer-events-none absolute left-[-9999px] top-0 size-px overflow-hidden opacity-0"
          aria-hidden
        >
          <MarketoForm formData={enrichmentFormData} callback={() => false} />
        </div>
      )}
    </div>
  );
};

export default FreeTrialFTUX;
